/* The zoom that puts the whole box on screen.
 *
 * `DEFAULT_CAMERA.zoom` is 1, and `createProjector` reads that as "one unit is half the smaller
 * viewport dimension". The unit cube is 2 across, so at zoom 1 an unrotated plane exactly fills
 * that dimension - and the plane is never unrotated. Yawed and pitched, its corners project past
 * the edge in both directions, and the box's own thickness and plinth reach further still.
 *
 * Measured at `DEFAULT_CAMERA` against the bounds of the drawn box, the overflow is:
 *
 *   1000x700   58px left, 12px right, 26px top, 41px bottom
 *   1700x900   33px top, 53px bottom
 *   700x700    208px left, 162px right
 *
 * So the box is cropped at every pane shape, and worst where the pane is square - which is the one
 * case the "smaller dimension governs" rule was supposed to protect.
 */

import type { CameraState, NumericRange, ProjectedPoint, Size } from '../types';
import { createProjector } from './projection';

/** The extent of the drawn box in the projector's own coordinates. */
export type UnitBounds = {
  x: NumericRange;
  y: NumericRange;
  z: NumericRange;
};

/**
 * Pixels to leave clear outside the box.
 *
 * A pixel count and not a fraction, because what lives out there does not scale with zoom: the
 * tick labels and axis titles are placed at fixed offsets from the box in screen space
 * (`renderAxes3d` hangs the numbers 8px off it and the titles 26px). A percentage margin would
 * over-reserve on a large pane and under-reserve on a small one, which is the shape of the bug
 * being fixed rather than a fix for it.
 *
 * One number per axis rather than four, because the fit keeps the box centred - see below - so
 * only the larger of each opposing pair can bind.
 */
export type FitInset = {
  horizontal: number;
  vertical: number;
};

/**
 * Pixels the fit keeps clear outside the box, for the chrome drawn out there.
 *
 * Measured off `renderAxes3d`: the horizontal tick numbers hang 8px below the box on an 11px font,
 * the axis titles 26px below that anchor on a 12px one, and the z title sits 10px above the top
 * corner. So the vertical budget is the deeper of those two stacks and the horizontal one covers
 * the 8px the z numbers are inset by.
 *
 * Not the *width* of a z tick label, which is the one thing out there that cannot be budgeted for
 * from a constant - it depends on whatever `format` produces, and `renderAxes2d` measures its text
 * for exactly this reason. Left out rather than guessed at: an under-reserved gutter clips a label,
 * and an over-reserved one shrinks the plot on every chart to protect the worst case of one.
 */
export const CHROME_INSET = {
  horizontal: 8,
  vertical: 44,
} as const satisfies FitInset;

type FitZoomArgs = {
  camera: CameraState;
  viewport: Size;
  bounds: UnitBounds;
  inset: FitInset;
};

const cornersOf = ({ x, y, z }: UnitBounds): [number, number, number][] =>
  x.flatMap(cx =>
    y.flatMap(cy => z.map((cz): [number, number, number] => [cx, cy, cz]))
  );

/**
 * The zoom at which the box's furthest corner still clears the inset.
 *
 * Measured at zoom 1 and scaled, which is exact rather than an approximation: `zoom` enters
 * `createProjector` only through `pixelsPerUnit`, and the foreshortening divisor is a function of
 * depth alone - so every screen offset from the centre is linear in zoom.
 *
 * Fitted about the centre rather than to the box's bounding rectangle. The projector has no pan:
 * it puts the unit cube's origin at the middle of the viewport, and z runs 0..1 upward from there,
 * so the box always sits high of centre. Fitting the rectangle would need a pan to go with it,
 * which is a larger change than this and one a reader would then have to be able to undo. Keeping
 * the centre costs a little zoom - 0.895 against 0.913 on a 1000x700 pane - and no new state.
 *
 * Returns the camera's own zoom when there is nothing to fit against: a viewport smaller than its
 * own insets has no room to reason about, and a degenerate box projects to a point.
 */
export const fitZoom = ({
  camera,
  viewport,
  bounds,
  inset,
}: FitZoomArgs): number => {
  const availableX = viewport.width / 2 - inset.horizontal;
  const availableY = viewport.height / 2 - inset.vertical;
  if (availableX <= 0 || availableY <= 0) return camera.zoom;

  const project = createProjector({ ...camera, zoom: 1 }, viewport);
  const centreX = viewport.width / 2;
  const centreY = viewport.height / 2;

  const offsets = cornersOf(bounds).map(([x, y, z]) => {
    const { screenX, screenY }: ProjectedPoint = project(x, y, z);
    return {
      horizontal: Math.abs(screenX - centreX),
      vertical: Math.abs(screenY - centreY),
    };
  });

  const reachX = Math.max(...offsets.map(({ horizontal }) => horizontal));
  const reachY = Math.max(...offsets.map(({ vertical }) => vertical));
  if (reachX <= 0 || reachY <= 0) return camera.zoom;

  return Math.min(availableX / reachX, availableY / reachY);
};
